import { useQuery } from "@tanstack/react-query"
import {
  type JourneyStep,
  pixelEventId,
  type SignupAnswerValue,
  type SignupDetailsRequest,
  type SignupErrorCode,
  type SignupPage as SignupPageData,
  type SignupState,
  signupDetailsRequestSchema,
  signupErrorCodeSchema,
} from "@ternary/contracts"
import {
  Button,
  type ButtonProps,
  Checkbox,
  EmptyState,
  Field,
  FormWizard,
  Input,
  Stepper,
  ThemeRoot,
  useToast,
  type WizardErrors,
  WizardFieldError,
  type WizardLabels,
  type WizardStep,
} from "@ternary/ui"
import { type ReactElement, useEffect, useEffectEvent, useId, useRef, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useParams, useSearchParams } from "react-router-dom"
import { firePixelEvent, installConsentedPixels } from "../analytics.js"
import {
  loadSignupPage,
  loadSignupState,
  SignupApiError,
  signupDiscordPath,
  startSignup,
  submitSignupDetails,
  verifySignupEmail,
} from "../api.js"
import { customerQueryKeys } from "../query-keys.js"
import { ConsentBanner } from "../signup/consent-banner.js"
import {
  chunkSignupFields,
  SignupFieldInput,
  signupAnswersToSend,
  validateSignupAnswers,
} from "../signup/fields.js"
import { forgetSignup, rememberSignup, signupIdFor } from "../signup/session.js"
import { SignupLoading, SignupShell } from "../signup/shell.js"
import {
  type ConsentChoice,
  ensureTouch,
  markFired,
  readConsent,
  rememberLanding,
  sendJourney,
} from "../signup/tracking.js"

/** Where the page leaves for Discord or the join page. A test replaces `assign`. */
export const signupNavigation = {
  assign(url: string) {
    window.location.assign(url)
  },
}

const RESEND_WAIT_SECONDS = 30
/**
 * Three questions to a page, not chunkSignupFields' four: with the cookie banner still shown and
 * every field in error, four do not fit at 1280×720 or 390×844 in Spanish, so the step is split
 * (ruling R25). The default free product's three fields stay one page.
 */
const QUESTIONS_PER_PAGE = 3
// /start waits this long at most for the journey events queued before it: they keep the CRM
// timeline in order, but tracking never blocks the signup.
const journeyWaitMs = 1_500

/** Resolves when `work` settles or after `ms`, whichever is first. Never rejects. */
function settledWithin(work: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms)
    work.then(
      () => {
        clearTimeout(timer)
        resolve()
      },
      () => {
        clearTimeout(timer)
        resolve()
      },
    )
  })
}

type SignupDraft = {
  firstName: string
  lastName: string
  email: string
  terms: boolean
  answers: Record<string, SignupAnswerValue>
  code: string
  /** The masked address the last code went to, from `POST …/details`. */
  otpSentTo: string
}

const EMPTY_DRAFT: SignupDraft = {
  answers: {},
  code: "",
  email: "",
  firstName: "",
  lastName: "",
  otpSentTo: "",
  terms: false,
}

// The draft keys the details POST sends, so a change on any details page sends them again.
const DETAILS_KEYS = ["firstName", "lastName", "email", "terms", "answers"] as const

// Refusals of the code that belong to the code field, so its input is marked invalid.
const CODE_FIELD_ERRORS = new Set<SignupErrorCode>([
  "code_invalid",
  "code_expired",
  "invalid_request",
  "not_found",
  "too_many_attempts",
])

type Translate = ReturnType<typeof useTranslation>["t"]

/**
 * Plain copy for each signup error code (§3.8). `during` says what was sent: a rate limit on the
 * details POST is the code-email limit, and a 404 on verify means the code belongs to a signup
 * this browser no longer holds.
 */
export function signupErrorMessage(
  t: Translate,
  code: SignupErrorCode | null,
  options: {
    attemptsRemaining?: number | null
    during?: "details" | "verify" | "confirm" | "other"
  } = {},
): string {
  const { attemptsRemaining = null, during = "other" } = options
  switch (code) {
    case "code_invalid":
      if (attemptsRemaining === null) {
        return t("That code isn't right. Check the latest email we sent.")
      }
      // The fifth wrong code answers 0: no try is left, so it reads as the lock it is (M4).
      return attemptsRemaining === 0
        ? signupErrorMessage(t, "too_many_attempts", { during })
        : t("That code isn't right. Tries left: {{tries}}.", { tries: attemptsRemaining })
    case "code_expired":
      return t("That code has expired. Send a new code.")
    case "too_many_attempts":
      // A held Discord link sends no new code: it starts again where the signup began (R73).
      return during === "confirm"
        ? t("Too many wrong codes. Start the Discord step again in the browser where you began.")
        : t("Too many wrong codes. Send a new code to try again.")
    case "too_many_sends":
      return t("We can't send more codes for this signup. Use the latest code we sent.")
    case "rate_limited":
      // On details it is the short write limit or a mailbox's hourly or daily one, so the copy
      // names no wait (M5).
      return during === "details"
        ? t("Too many codes requested. Try again later.")
        : t("Too many attempts. Wait a minute and try again.")
    case "not_found":
      return during === "verify"
        ? t("This code is for an earlier signup. Send a new code.")
        : t("This signup page isn't available any more.")
    case "invalid_request":
      return during === "verify"
        ? t("Enter the 6-digit code.")
        : t("Check your details and try again.")
    case "wrong_step":
      return t("This signup has already moved on. Reload the page to continue.")
    case "unavailable":
      return t("Signup is unavailable right now. Try again in a few minutes.")
    case "email_unavailable":
      return t("We couldn't send the email right now. Try again in a few minutes.")
    case "discord_denied":
      // A cancelled consent, or a code Discord refused: either way nothing was linked (m1).
      return t("The Discord login didn't finish, so nothing changed.")
    case "discord_in_use":
      // Short: with the hint it is the longest alert on the step-1 and Discord pages (R25).
      return t("Another member already uses that Discord account. Use a different one.")
    case "discord_link_expired":
      return t(
        "This Discord sign-in expired. Start the Discord step again in the browser where you began.",
      )
    case "discord_not_linked":
      return t("Connect Discord first, then join.")
    case "guild_unavailable":
      return t("The Discord server isn't available right now. Try again in a few minutes.")
    case "join_failed":
      return t("We couldn't add you to the Discord server. Try again.")
    case "not_signed_in":
      return t("Log in with Discord to continue.")
    case null:
      return t("Something went wrong. Try again.")
  }
}

/**
 * What to add after a code's copy: for a Discord account another member holds, where to get help,
 * since only the tenant can unlink it.
 */
export function signupErrorHint(
  t: Translate,
  code: SignupErrorCode | null,
  company: string,
): string | null {
  return code === "discord_in_use"
    ? t("Is it yours? Contact {{company}} support.", { company })
    : null
}

type Toast = ReturnType<typeof useToast>["toast"]

/**
 * Resend, rendered inside the signup's ThemeRoot, so the toast it sends comes from the shell's
 * ToastProvider and takes the tenant's theme (m12).
 */
function ResendButton({
  onResend,
  ...props
}: Omit<ButtonProps, "onClick"> & { onResend(toast: Toast): void }) {
  const { toast } = useToast()
  return <Button {...props} onClick={() => onResend(toast)} />
}

/** A failure already worded for the visitor, such as a refused start inside a details send. */
class SignupCopyError extends Error {
  override name = "SignupCopyError"
}

function errorCopy(t: Translate, error: unknown, during: "details" | "verify" | "other") {
  if (error instanceof SignupCopyError) return error.message
  return error instanceof SignupApiError
    ? signupErrorMessage(t, error.code, { attemptsRemaining: error.attemptsRemaining, during })
    : signupErrorMessage(t, null)
}

/** Where a held signup resumes: the code step once its code was sent, Connect Discord once verified. */
function resumeStepOf(state: SignupState | null): "code" | "discord" | undefined {
  if (state?.step === "discord") return "discord"
  if (state?.step === "verify_email" || (state?.step === "details" && state.email)) return "code"
  return undefined
}

/** The masked address in the wizard's stored draft, so a reload still knows the code was sent. */
function storedOtpSentTo(slug: string): string {
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(`tny_signup_draft:${slug}`) ?? "null")
    const sentTo = (stored as { otpSentTo?: unknown } | null)?.otpSentTo
    return typeof sentTo === "string" ? sentTo : ""
  } catch {
    return ""
  }
}

const ICON_PATHS = {
  check: "M5 12.5l4.5 4.5L19 7.5",
  external: "M14 4h6v6M20 4l-9 9M18 14v6H4V6h6",
  mail: "M4 6h16v12H4zM4 7l8 6 8-6",
  server: "M4 4h16v6H4zM4 14h16v6H4zM8 7h.01M8 17h.01",
  shield: "M12 3l8 3v6c0 4.4-3.4 8.2-8 9-4.6-.8-8-4.6-8-9V6z",
  user: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0",
}

/** A decorative line icon, sized to the text. */
export function Icon({ name }: { name: keyof typeof ICON_PATHS }) {
  return (
    <svg
      aria-hidden="true"
      className="signup-icon"
      fill="none"
      height="1.25em"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="1.25em"
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  )
}

/** The unknown-slug and failed-load states, in the default theme: the tenant's brand is unknown. */
function SignupUnavailable({ error, onRetry }: { error: unknown; onRetry(): void }) {
  const { t } = useTranslation()
  const missing = error instanceof SignupApiError && error.status === 404
  return (
    <ThemeRoot className="signup-shell">
      <main className="signup-main signup-state">
        {/* The page's one h1, hidden: EmptyState's title is an h2 (M10). */}
        <h1 className="tny-visually-hidden">{t("Signup")}</h1>
        <EmptyState
          action={missing ? undefined : <Button onClick={onRetry}>{t("Retry")}</Button>}
          description={
            missing
              ? t("Check the link you followed and try again.")
              : t("Check your connection and try again.")
          }
          title={
            missing ? t("This signup page isn't available") : t("We couldn't load this signup page")
          }
        />
      </main>
    </ThemeRoot>
  )
}

/**
 * `/signup/:slug`, signup step 1: the visitor's details, the tenant's questions four to a page, and
 * the 6-digit email code, then Connect Discord. A signup this browser already holds resumes from
 * its server state instead of starting again.
 */
export function SignupPage(): ReactElement {
  const { slug = "" } = useParams()
  const [heldId] = useState(() => signupIdFor(slug))
  const page = useQuery({
    queryFn: ({ signal }) => loadSignupPage(slug, { signal }),
    queryKey: customerQueryKeys.signupPage(slug),
  })
  // Without the state (an error, or before Track A's route ships), the wizard's stored draft and
  // the ?step= parameter resume instead.
  const state = useQuery({
    enabled: heldId !== null,
    queryFn: ({ signal }) => loadSignupState(heldId ?? "", { signal }),
    queryKey: customerQueryKeys.signupState(heldId ?? ""),
    retry: false,
    staleTime: 0,
  })
  // Past Discord, or a Discord account held for confirmation (R73): the join page takes over.
  const joining =
    heldId !== null &&
    ["join", "done", "confirm_discord", "confirm_switch"].includes(state.data?.step ?? "")
  // A Discord hold this browser also held expired (R75 N4): A answers 410 once and ends the hold,
  // so the state is read again, now the signup's own, and the expiry is said on its step.
  const [holdExpired, setHoldExpired] = useState(false)
  const expiredNow =
    state.error instanceof SignupApiError && state.error.code === "discord_link_expired"
  useEffect(() => {
    if (!expiredNow) return
    setHoldExpired(true)
    void state.refetch()
  }, [expiredNow, state.refetch])
  // A re-link from the join page comes back here with ?error= (A sends every Discord return to
  // the signup page), so a valid code goes along to the join page (I2).
  const [searchParams] = useSearchParams()
  const returnError = signupErrorCodeSchema.safeParse(searchParams.get("error"))
  const forwardError = returnError.success
    ? returnError.data
    : holdExpired
      ? "discord_link_expired"
      : null
  const joinPath = heldId
    ? `/join/${encodeURIComponent(heldId)}${forwardError ? `?error=${forwardError}` : ""}`
    : ""

  useEffect(() => {
    if (joining) signupNavigation.assign(joinPath)
  }, [joinPath, joining])

  if (
    page.isPending ||
    (heldId !== null && state.isPending) ||
    (expiredNow && !holdExpired) ||
    (holdExpired && state.isFetching) ||
    joining
  ) {
    return <SignupLoading />
  }
  if (page.isError) {
    return <SignupUnavailable error={page.error} onRetry={() => void page.refetch()} />
  }
  return (
    <SignupFlow
      heldId={heldId}
      holdExpired={holdExpired}
      page={page.data}
      resume={state.data ?? null}
      slug={slug}
    />
  )
}

function SignupFlow({
  page,
  slug,
  heldId,
  resume,
  holdExpired,
}: {
  page: SignupPageData
  slug: string
  heldId: string | null
  resume: SignupState | null
  /** The state answered 410 discord_link_expired before its re-read (R75 N4). */
  holdExpired: boolean
}) {
  const { i18n, t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const urlStep = searchParams.get("step") ?? undefined
  const resumeStep = resumeStepOf(resume)
  // A resume opens the server's step, until the wizard moves or the URL changes from the one the
  // page loaded with (browser Back and Forward).
  const [loadedUrlStep] = useState(urlStep)
  const [moved, setMoved] = useState(false)
  const stepId =
    resumeStep === undefined || moved || urlStep !== loadedUrlStep ? urlStep : resumeStep

  const [verified, setVerified] = useState(resumeStep === "discord")
  const [signupId, setSignupId] = useState(heldId)
  const signupIdRef = useRef(heldId)
  const starting = useRef<Promise<string> | null>(null)
  const [touchId, setTouchId] = useState<string>()
  // The one landing call per load, which /start waits for (see touchReady).
  const landing = useRef<Promise<void> | null>(null)
  // customer-api stamps journey events on arrival, so this page sends them one at a time, in the
  // order the visitor lived them; /start (form_start) waits for every event queued before it.
  const journey = useRef<Promise<void>>(Promise.resolve())
  const firstInputSeen = useRef(false)
  const [ads, setAds] = useState(() => readConsent()?.ads === true)
  const [otpSentTo, setOtpSentTo] = useState(() => resume?.email ?? storedOtpSentTo(slug))
  // When "Resend code" is allowed again: 30 s after a code was sent from this page.
  const [resendAt, setResendAt] = useState(0)
  const [, setClock] = useState(0)
  const secondsLeft = Math.max(0, Math.ceil((resendAt - Date.now()) / 1_000))
  const [resending, setResending] = useState(false)
  // Set while a new code is being sent or a code verified, so the two never race (M1).
  const resendingRef = useRef(false)
  const [verifying, setVerifying] = useState(false)
  const verifyingRef = useRef(false)
  const [resendError, setResendError] = useState("")
  const resendWaitId = useId()
  const wizardRef = useRef<HTMLDivElement>(null)
  const pageViews = useRef(new Set<JourneyStep>())
  const stepViews = useRef(new Set<JourneyStep>())

  const currentStep = stepId ?? (verified ? "discord" : "you")
  // Connect Discord opens only once the email is verified (every step before it then skips), so
  // the phase follows that, never a stale ?step= that the wizard clamps away (M6).
  const phase: JourneyStep = verified ? "discord" : "details"
  const phaseRef = useRef(phase)
  // The resumed state speaks only for its own signup, not for one started after a 404 (M3).
  const detailsSent =
    otpSentTo !== "" || (resume !== null && resume.step !== "details" && signupId === heldId)
  const company = page.branding.companyName
  const errorParam = signupErrorCodeSchema.safeParse(
    searchParams.get("error") ?? (holdExpired ? "discord_link_expired" : null),
  )

  /**
   * Records the session's touch id, and the landing once per load. Resolves once that one landing
   * call was answered or failed.
   */
  const touchReady = (id: string | undefined): Promise<void> => {
    if (!id) return Promise.resolve()
    setTouchId(id)
    landing.current ??= queueJourney({ event: "landing", touchId: id })
    // The step on screen (read when the touch arrives, not when it was asked for) is queued right
    // behind the landing, so a /start that waits for the queue never overtakes it.
    queueStepView(phaseRef.current, { signupId: signupIdRef.current, touchId: id })
    return landing.current
  }

  /** Queues one step view per signup step per load. */
  const queueStepView = (
    step: JourneyStep,
    ids: { signupId: string | null | undefined; touchId: string | undefined },
  ) => {
    if (stepViews.current.has(step) || (!ids.touchId && !ids.signupId)) return
    stepViews.current.add(step)
    void queueJourney({
      event: "step_view",
      step,
      ...(ids.signupId ? { signupId: ids.signupId } : {}),
      ...(ids.touchId ? { touchId: ids.touchId } : {}),
    })
  }

  /**
   * Sends `body` once every journey event queued before it was answered (or failed), or after
   * journeyWaitMs, whichever is first: a slow event never holds the ones behind it for long.
   */
  const queueJourney = (body: Parameters<typeof sendJourney>[0]): Promise<void> => {
    const sent = settledWithin(journey.current, journeyWaitMs).then(() => sendJourney(body))
    journey.current = sent
    return sent
  }

  /**
   * The signup id: the held one, or a new `POST …/start` (only one at a time). It waits for the
   * touch first, because the server links the signup to the touch through the cookie the touch
   * sets (R63); a visitor who has not answered the banner is posted with ads:false.
   */
  const ensureSignupId = () => {
    if (signupIdRef.current) return Promise.resolve(signupIdRef.current)
    starting.current ??= ensureTouch()
      .then(async (touch) => {
        // customer-api stamps journey events on arrival, so /start (form_start) waits for the
        // landing and every step view queued before it: the CRM timeline keeps the order the
        // visitor lived. Tracking never blocks the signup, so it waits for them at most
        // journeyWaitMs.
        void touchReady(touch)
        await settledWithin(journey.current, journeyWaitMs)
        return startSignup(slug, touch)
      })
      .then(({ signupId: id }) => {
        rememberSignup(slug, id)
        signupIdRef.current = id
        setSignupId(id)
        return id
      })
      .finally(() => {
        starting.current = null
      })
    return starting.current
  }

  /** Holds `id`, or none. This page has sent no details for it yet, so no Lead fires (M3). */
  const holdSignup = (id: string | null) => {
    signupIdRef.current = id
    setSignupId(id)
    setOtpSentTo("")
  }

  const forgetHeldSignup = (id: string) => {
    forgetSignup(slug, id)
    holdSignup(null)
  }

  /**
   * Runs `send` with the signup id. A 404 means the shared cookie names another signup: the one
   * another tab has stored since, when there is one (M7), or else none, so it starts once more.
   */
  const withSignup = async <T,>(send: (id: string) => Promise<T>): Promise<T> => {
    // A refused start is worded as a start: its rate limit is not the code-email limit.
    const started = () =>
      ensureSignupId().catch((error: unknown) => {
        throw new SignupCopyError(errorCopy(t, error, "other"))
      })
    const id = await started()
    try {
      return await send(id)
    } catch (error) {
      if (!(error instanceof SignupApiError && error.code === "not_found")) throw error
      const stored = signupIdFor(slug)
      if (stored && stored !== id) {
        holdSignup(stored)
        return send(stored)
      }
      forgetHeldSignup(id)
      return send(await started())
    }
  }

  const onLoad = useEffectEvent(() => {
    rememberLanding(new URL(window.location.href), document.referrer)
    // With a stored choice the touch posts now; otherwise once the banner or the first input asks.
    if (readConsent()) void ensureTouch().then(touchReady)
  })
  useEffect(() => onLoad(), [])

  /**
   * The first input starts the signup (R63). It runs from React's onInput: a browser settles the
   * microtask queue after each DOM listener, so a DOM listener on the wizard ran before React's own
   * on the root, and the touch it put in state re-rendered the field from the draft before React
   * read the typed text, which wiped it.
   */
  const onFirstInput = () => {
    if (firstInputSeen.current) return
    firstInputSeen.current = true
    void ensureSignupId().catch(() => undefined)
  }

  // One PageView per signup step per load, once the visitor accepted ad cookies.
  useEffect(() => {
    if (!ads || pageViews.current.has(phase)) return
    pageViews.current.add(phase)
    installConsentedPixels(page.pixels)
    firePixelEvent(page.pixels, { name: "PageView", path: window.location.pathname })
  }, [ads, page.pixels, phase])

  // Lead once the details were sent, at most once per signup on this device. Only a consenting
  // visitor marks it, so a decline now never stops an Accept later from firing it (R67).
  useEffect(() => {
    if (!ads || !detailsSent || !signupId || readConsent()?.ads !== true) return
    installConsentedPixels(page.pixels)
    const eventId = pixelEventId(signupId, "Lead")
    if (markFired(eventId)) firePixelEvent(page.pixels, { eventId, name: "Lead" })
  }, [ads, detailsSent, page.pixels, signupId])

  // A later step's view (and the first one of a load that holds a signup but no touch), once there
  // is a touch or a signup to carry it. touchReady queues the first step's.
  // A resumed signup's view goes at once on its own; a later step's once it is shown.
  const onStepShown = useEffectEvent(queueStepView)
  useEffect(() => onStepShown(phase, { signupId, touchId }), [phase, signupId, touchId])
  // touchReady reads the step on screen when a slow touch arrives, not the one it began on.
  useEffect(() => {
    phaseRef.current = phase
  }, [phase])

  // Re-renders each second until a resend is allowed again.
  useEffect(() => {
    if (resendAt <= Date.now()) return
    const timer = setInterval(() => {
      setClock((tick) => tick + 1)
      if (Date.now() >= resendAt) clearInterval(timer)
    }, 1_000)
    return () => clearInterval(timer)
  }, [resendAt])

  const onConsent = (choice: ConsentChoice) => {
    setAds(choice.ads)
    // writeConsent already posted the touch with this choice (or patched it); this shares it.
    void ensureTouch().then(touchReady)
    // The clicked button is gone, so focus moves to the form (Task 16 M3).
    const area = wizardRef.current
    const field = [
      ...(area?.querySelectorAll<HTMLElement>(
        ".tny-wizard-body :is(input, select, textarea, button):not(:disabled)",
      ) ?? []),
    ].find((element) => !element.closest("[popover]"))
    ;(field ?? area?.querySelector<HTMLElement>(".tny-wizard-step-title"))?.focus()
  }

  const youErrors = (draft: SignupDraft): WizardErrors => {
    const errors: WizardErrors = {}
    if (!draft.firstName.trim()) errors.firstName = t("Fill in your first name.")
    if (!draft.lastName.trim()) errors.lastName = t("Fill in your last name.")
    if (!draft.email.trim()) errors.email = t("Enter your email address.")
    else if (!signupDetailsRequestSchema.shape.email.safeParse(draft.email.trim()).success) {
      errors.email = t("Enter an email address, such as name@example.com.")
    }
    if (!draft.terms) errors.terms = t("Agree to the terms to continue.")
    return errors
  }

  /** Sends (or resends) the details, which emails a new code. Throws the plain copy. */
  const sendDetails = async (draft: SignupDraft) => {
    const details: SignupDetailsRequest = {
      answers: signupAnswersToSend(page.fields, draft.answers),
      consent: { ads: readConsent()?.ads === true, terms: true },
      email: draft.email.trim(),
      firstName: draft.firstName.trim(),
      lastName: draft.lastName.trim(),
    }
    try {
      const { otpSentTo: sentTo } = await withSignup((id) => submitSignupDetails(id, details))
      setOtpSentTo(sentTo)
      setResendAt(Date.now() + RESEND_WAIT_SECONDS * 1_000)
      setResendError("")
      return sentTo
    } catch (error) {
      throw new Error(errorCopy(t, error, "details"))
    }
  }

  const verifyCode = async (code: string) => {
    const id = signupIdRef.current
    if (!id) {
      throw new WizardFieldError("code", {
        code: t("This code is for an earlier signup. Send a new code."),
      })
    }
    // A resend in flight replaces the code, so verifying this one would only spend a try (M1).
    if (resendingRef.current) {
      throw new WizardFieldError("code", {
        code: t("A new code is on its way. Enter it once it arrives."),
      })
    }
    verifyingRef.current = true
    setVerifying(true)
    try {
      try {
        await verifySignupEmail(id, code)
      } catch (error) {
        // The shared cookie may name the signup another tab stored since (M7): the code goes to it
        // once. With none, there is no code to retry for a new signup, so this one is forgotten.
        const stored = signupIdFor(slug)
        if (
          !(
            error instanceof SignupApiError &&
            error.code === "not_found" &&
            stored &&
            stored !== id
          )
        ) {
          throw error
        }
        holdSignup(stored)
        await verifySignupEmail(stored, code)
      }
    } catch (error) {
      if (error instanceof SignupApiError && error.code === "not_found") {
        forgetHeldSignup(signupIdRef.current ?? id)
      }
      const copy = errorCopy(t, error, "verify")
      if (error instanceof SignupApiError && error.code && CODE_FIELD_ERRORS.has(error.code)) {
        throw new WizardFieldError("code", { code: copy })
      }
      throw new Error(copy)
    } finally {
      verifyingRef.current = false
      setVerifying(false)
    }
    setVerified(true)
  }

  const resend = async (draft: SignupDraft, goTo: (stepId: string) => void, toast: Toast) => {
    // Resumed without the details (another tab or device): they are asked for again.
    if (Object.keys(youErrors(draft)).length > 0) {
      goTo("you")
      return
    }
    // A verify in flight would be answered by the code this replaces (M1).
    if (verifyingRef.current) return
    resendingRef.current = true
    setResending(true)
    setResendError("")
    try {
      const sentTo = await sendDetails(draft)
      toast({ message: t("We sent a new code to {{email}}.", { email: sentTo }), tone: "success" })
    } catch (error) {
      setResendError(error instanceof Error ? error.message : String(error))
    } finally {
      resendingRef.current = false
      setResending(false)
    }
  }

  const questionPages = chunkSignupFields(page.fields, QUESTIONS_PER_PAGE)
  const lastDetails = questionPages.length > 0 ? `questions-${questionPages.length}` : "you"
  const steps: WizardStep<SignupDraft>[] = [
    {
      description: t("We'll email you a 6-digit code to confirm it's you."),
      fields: lastDetails === "you" ? DETAILS_KEYS : ["firstName", "lastName", "email", "terms"],
      id: "you",
      nextLabel: lastDetails === "you" ? t("Email me a code") : undefined,
      render: ({ draft, errors, setDraft }) => (
        <>
          {/*
            Discord handed back to a browser that holds no signup: say what happened (m2). No
            support hint here, so the tallest step still fits with the banner (R25); the Discord
            step shows it if the account is refused again.
          */}
          {errorParam.success && heldId === null ? (
            <p className="tny-error" role="alert">
              {`${signupErrorMessage(t, errorParam.data)} ${t("Start again here.")}`}
            </p>
          ) : null}
          <div className="signup-name-row">
            <Field error={errors.firstName} label={t("First name")} required>
              <Input
                autoComplete="given-name"
                maxLength={120}
                onChange={(event) => setDraft({ firstName: event.currentTarget.value })}
                value={draft.firstName}
              />
            </Field>
            <Field error={errors.lastName} label={t("Last name")} required>
              <Input
                autoComplete="family-name"
                maxLength={120}
                onChange={(event) => setDraft({ lastName: event.currentTarget.value })}
                value={draft.lastName}
              />
            </Field>
          </div>
          <Field error={errors.email} label={t("Email")} required>
            <Input
              autoComplete="email"
              inputMode="email"
              maxLength={254}
              onChange={(event) => setDraft({ email: event.currentTarget.value })}
              type="email"
              value={draft.email}
            />
          </Field>
          <Checkbox
            checked={draft.terms}
            error={errors.terms}
            label={
              <Trans
                components={{
                  privacy: (
                    // biome-ignore lint/a11y/useAnchorContent: Trans fills it with "Privacy Policy".
                    <a
                      className="signup-link"
                      href={page.legal.privacyUrl}
                      rel="noreferrer"
                      target="_blank"
                    />
                  ),
                  terms: (
                    // biome-ignore lint/a11y/useAnchorContent: Trans fills it with "Terms of Service".
                    <a
                      className="signup-link"
                      href={page.legal.termsUrl}
                      rel="noreferrer"
                      target="_blank"
                    />
                  ),
                }}
                i18nKey="I agree to the <terms>Terms of Service</terms> and <privacy>Privacy Policy</privacy>"
                t={t}
              />
            }
            onChange={(event) => setDraft({ terms: event.currentTarget.checked })}
            required
          />
        </>
      ),
      skip: () => verified,
      title: t("Your details"),
      validate: youErrors,
    },
    ...questionPages.map(
      (fields, index): WizardStep<SignupDraft> => ({
        description: t("{{company}} asks everyone these, so the team can welcome you.", {
          company,
        }),
        fields: index === questionPages.length - 1 ? DETAILS_KEYS : ["answers"],
        id: `questions-${index + 1}`,
        nextLabel: index === questionPages.length - 1 ? t("Email me a code") : undefined,
        render: ({ draft, errors, setDraft }) =>
          fields.map((field) => (
            <SignupFieldInput
              error={errors[field.key]}
              field={field}
              key={field.key}
              onChange={(value) =>
                setDraft((current) => ({
                  ...current,
                  answers: { ...current.answers, [field.key]: value },
                }))
              }
              value={draft.answers[field.key]}
            />
          )),
        skip: () => verified,
        title: t("A few more details"),
        validate: (draft) =>
          Object.fromEntries(
            Object.entries(validateSignupAnswers(fields, draft.answers)).map(([key, message]) => [
              key,
              t(message),
            ]),
          ),
      }),
    ),
    {
      description: otpSentTo
        ? t("We sent a 6-digit code to {{email}}.", { email: otpSentTo })
        : t("We sent a 6-digit code to your email."),
      fields: ["code"],
      id: "code",
      nextLabel: t("Verify email"),
      render: ({ draft, errors, goTo, setDraft }) => (
        <>
          <Field
            error={errors.code}
            hint={t("It works for 10 minutes.")}
            label={t("6-digit code")}
            required
          >
            <Input
              autoComplete="one-time-code"
              className="signup-code-input"
              inputMode="numeric"
              onChange={(event) =>
                setDraft({ code: event.currentTarget.value.replace(/\D/g, "").slice(0, 6) })
              }
              pattern="\d{6}"
              value={draft.code}
            />
          </Field>
          <p className="signup-resend">
            {t("Didn't get it? Check your spam folder, or")}
            <ResendButton
              aria-describedby={secondsLeft > 0 ? resendWaitId : undefined}
              disabled={secondsLeft > 0 || verifying}
              loading={resending}
              onResend={(toast) => void resend(draft, goTo, toast)}
              size="sm"
              variant="ghost"
            >
              {t("Resend code")}
              {secondsLeft > 0 ? (
                <span aria-hidden="true">{` 0:${String(secondsLeft).padStart(2, "0")}`}</span>
              ) : null}
            </ResendButton>
            {/* The countdown is hidden from assistive technology, so this says it (M11). */}
            {secondsLeft > 0 ? (
              <span className="tny-visually-hidden" id={resendWaitId}>
                {t("Available in {{time}}.", {
                  time: new Intl.NumberFormat(i18n.resolvedLanguage, {
                    style: "unit",
                    unit: "second",
                    unitDisplay: "long",
                  }).format(secondsLeft),
                })}
              </span>
            ) : null}
          </p>
          {resendError ? (
            <p className="tny-error" role="alert">
              {resendError}
            </p>
          ) : null}
        </>
      ),
      skip: () => verified,
      title: t("Check your email"),
      validate: (draft) =>
        /^\d{6}$/.test(draft.code) ? {} : { code: t("Enter the 6-digit code.") },
    },
    {
      description: t("{{company}} uses Discord to let you into its server.", { company }),
      id: "discord",
      render: () => (
        <>
          {otpSentTo ? (
            <p className="signup-confirmed">
              <Icon name="check" />
              {t("Email confirmed: {{email}}", { email: otpSentTo })}
            </p>
          ) : null}
          <div className="signup-note">
            <p className="signup-note-title">{t("Discord will ask you to share:")}</p>
            <ul className="signup-share-list">
              <li>
                <Icon name="user" />
                {t("Your Discord username and avatar")}
              </li>
              <li>
                <Icon name="mail" />
                {t("The email on your Discord account")}
              </li>
              <li>
                <Icon name="server" />
                {t("Permission to add you to the {{company}} server", { company })}
              </li>
            </ul>
          </div>
          <p className="signup-assurance">
            <Icon name="shield" />
            {t("We never send your Discord details to ad networks.")}
          </p>
          {errorParam.success ? (
            <p className="tny-error" role="alert">
              {[
                signupErrorMessage(t, errorParam.data),
                signupErrorHint(t, errorParam.data, company),
              ]
                .filter(Boolean)
                .join(" ")}
            </p>
          ) : null}
        </>
      ),
      title: t("Connect Discord"),
    },
  ]

  const onStepSubmit = async (id: string, draft: SignupDraft) => {
    if (id === lastDetails) return { code: "", otpSentTo: await sendDetails(draft) }
    if (id === "code") {
      await verifyCode(draft.code)
      // The spent code leaves the stored draft and the step's snapshot (M2).
      return { code: "" }
    }
    return undefined
  }

  const continueWithDiscord = () => {
    const id = signupIdRef.current
    if (!id) throw new Error(t("This signup page isn't available any more."))
    rememberSignup(slug, id)
    signupNavigation.assign(signupDiscordPath(id))
  }

  const stepOf: WizardLabels["stepOf"] = (current, total, title) =>
    t("Step {{current}} of {{total}}: {{title}}", { current, title, total })
  const labels: WizardLabels = {
    back: currentStep === "code" ? t("Use a different email") : t("Back"),
    completed: t("completed"),
    next: t("Continue"),
    stepOf,
  }

  return (
    <SignupShell
      banner={<ConsentBanner onChoice={onConsent} privacyUrl={page.legal.privacyUrl} />}
      page={page}
    >
      <Stepper
        completedIds={phase === "discord" ? ["details"] : []}
        completedLabel={t("completed")}
        currentId={phase}
        label={t("Signup progress")}
        stepOf={stepOf}
        steps={[
          { id: "details", label: t("Details") },
          { id: "discord", label: t("Discord") },
          { id: "join", label: t("Join") },
        ]}
      />
      <div className="signup-flow" onInput={onFirstInput} ref={wizardRef}>
        <FormWizard
          initialDraft={EMPTY_DRAFT}
          initialStepTrusted={resumeStep !== undefined}
          labels={labels}
          onStepChange={(id) => {
            setMoved(true)
            setSearchParams((params) => {
              const next = new URLSearchParams(params)
              // Back from the code step is "Use a different email": it opens the email field's
              // step, not the last page of questions. Only Back leaves the code step for it.
              next.set("step", currentStep === "code" && id === lastDetails ? "you" : id)
              return next
            })
          }}
          onStepSubmit={onStepSubmit}
          onSubmit={continueWithDiscord}
          persistKey={`tny_signup_draft:${slug}`}
          showStepper={false}
          stepId={stepId}
          steps={steps}
          submitLabel={t("Continue with Discord")}
          title={t("Join {{product}}", { product: page.product.name })}
        />
      </div>
    </SignupShell>
  )
}
